import { useCallback, useEffect, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'

import { api, fehlertext } from '../api'
import { useAktion, useLaden } from '../hooks'
import FehlendeArtikel from '../komponenten/FehlendeArtikel'
import Rueckfragen from '../komponenten/Rueckfragen'
import Meldung from '../komponenten/Meldung'
import Meldungen from '../komponenten/Meldungen'
import { istKorrigiert } from '../komponenten/SelectLineWarnungen'
import Zweitblick from '../komponenten/Zweitblick'
import { datum, differenz, menge, tonnen, zeitpunkt } from '../format'
import { laufPfad } from '../pfade'
import { IMPORTSTATUS } from '../texte'
import type { DoppelterLauf, FreigabeAntwort, ImportLauf, ImportLaufKurz, ImportPosition, Schwere } from '../typen'

/** So steht es an einem Lauf, den die Plattform aus SelectLine selbst gebucht hat (seit 18.10.2026). */
const SELBST_GEBUCHT = 'automatisch (SelectLine)'

const BEFUNDTITEL: Record<Schwere, string> = { fehler: 'Fehler', warnung: 'Warnung', hinweis: 'Hinweis' }

const ROLLENTEXT: Record<string, string> = {
  fertigartikel_produktion: 'Produktion',
  materialverbrauch: 'Verbrauch',
  zugang: 'Zugang',
  zaehlbestand: 'Zählbestand',
  sollbestand: 'Sollbestand',
}

/** Der eine Satz zum Doppel-Import - steht als Meldung am Lauf und noch einmal in der Rückfrage vor der Freigabe. */
function doppeltText(doppelt: DoppelterLauf[]): string {
  const laeufe = doppelt
    .map((d) => `Import ${d.lauf_id} (${IMPORTSTATUS[d.status]?.text ?? d.status}, ${zeitpunkt(d.angelegt_am)})`)
    .join(' und ')
  return `Diese Datei wurde schon als ${laeufe} geladen. Schon gebuchte Positionen werden bei der Freigabe übersprungen.`
}

/**
 * Bucht die Freigabe diese Position? Spiegelt die Bedingung von `lauf_buchen` im Backend: ohne Tag, ohne
 * Tonnen oder als reiner Vergleichswert wird nichts gebucht - das Backend meldet es als Hinweis „wird nicht
 * gebucht". Vorher zählte die Fußzeile „71 von 71", während oben „11 Position(en) werden nicht gebucht" stand.
 */
function wirdGebucht(position: ImportPosition): boolean {
  return (
    position.uebernehmen &&
    position.tag !== null &&
    position.menge_t !== null &&
    position.rolle !== 'sollbestand' &&
    !(position.hinweis ?? '').includes('nicht gebucht')
  )
}

export default function ImporteSeite() {
  const { id } = useParams()
  const navigate = useNavigate()
  const [lauf, setLauf] = useState<ImportLauf | null>(null)
  const [abgewaehlt, setAbgewaehlt] = useState<Set<number>>(new Set())
  /** Was die Freigabe übersprungen hat (schon gebucht) - bleibt stehen, bis ein anderer Lauf geladen wird. */
  const [freigabeHinweise, setFreigabeHinweise] = useState<string[]>([])
  /** Rückfragen, die bei der Freigabe noch offen waren und jetzt „übergangen“ sind (02.11.2026). */
  const [uebergangen, setUebergangen] = useState<NonNullable<FreigabeAntwort['rueckfragen_uebergangen']>>([])
  const { fehler, erfolg, arbeitet, setFehler, setErfolg, ausfuehren } = useAktion()
  const { daten: geladeneListe, fehler: listeFehler, neuLaden: listeLaden } = useLaden<ImportLaufKurz[]>(() => api.importe(), [])
  const liste = geladeneListe ?? []

  // Bleibt von Hand: ein Excel-Lauf wird nicht angezeigt, sondern auf seine Plan-Seite umgeleitet.
  const laufLaden = useCallback(() => {
    if (!id) {
      setLauf(null)
      return
    }
    setFehler(null)
    api
      .importLauf(Number(id))
      .then((wert) => {
        if (wert.dokumenttyp === 'excel') {
          navigate(laufPfad(wert), { replace: true })
          return
        }
        setLauf(wert)
        setAbgewaehlt(new Set())
        setFreigabeHinweise([])
        setUebergangen([])
      })
      .catch((f) => setFehler(fehlertext(f)))
  }, [id, navigate, setFehler])

  useEffect(laufLaden, [laufLaden])

  // Von der Liste in einen Lauf: oben anfangen. Sonst bleibt die Scrollposition der Liste stehen
  // und der Kopf mit „Zurück zur Liste" liegt außerhalb des Bilds.
  useEffect(() => {
    window.scrollTo(0, 0)
  }, [id])

  function neuPruefen() {
    if (!lauf) return
    void ausfuehren(async () => {
      const neu = await api.neuPruefen(lauf.id)
      setLauf(neu)
      setAbgewaehlt(new Set())
      listeLaden()
      setErfolg(
        neu.status === 'gesperrt'
          ? 'Erneut geprüft - der Lauf bleibt gesperrt. Die Befunde stehen unten.'
          : 'Erneut geprüft - der Lauf ist jetzt freigebbar.',
      )
    })
  }

  function freigeben() {
    if (!lauf) return
    // Dieselbe Datei ein zweites Mal: kein Fehler, aber eine bewusste Entscheidung - deshalb die Rückfrage.
    const doppelt = lauf.doppelt ?? []
    if (doppelt.length > 0 && !window.confirm(`${doppeltText(doppelt)} Trotzdem freigeben?`)) return
    void ausfuehren(async () => {
      const ergebnis = await api.freigeben(lauf.id, [...abgewaehlt])
      const bestellungen = ergebnis.bestellungen ?? []
      setFreigabeHinweise(ergebnis.hinweise ?? [])
      setUebergangen(ergebnis.rueckfragen_uebergangen ?? [])
      setErfolg(
        `${ergebnis.buchungen} Buchung(en) übernommen.` +
          (ergebnis.uebersprungen ? ` ${ergebnis.uebersprungen} Position(en) übersprungen, weil schon gebucht.` : '') +
          (bestellungen.length > 0
            ? ' ' +
              bestellungen
                .map((b) =>
                  b.rest_t > 0
                    ? `Bestellung ${b.id} (${b.material}) um ${tonnen(b.geliefert_t)} t gekürzt, Rest ${tonnen(b.rest_t)} t.`
                    : `Bestellung ${b.id} (${b.material}) ist damit geliefert.`,
                )
                .join(' ')
            : ''),
      )
      listeLaden()
      setLauf(await api.importLauf(lauf.id))
    })
  }

  function verwerfen() {
    if (!lauf) return
    void ausfuehren(async () => {
      await api.verwerfen(lauf.id)
      listeLaden()
      setLauf(await api.importLauf(lauf.id))
    })
  }

  /** Eine gebuchte Freigabe zurückdrehen - der einzige bestandswirksame Vorgang hatte bis zum
   * 19.09.2026 keinen Rückweg. */
  function zuruecknehmen() {
    if (!lauf) return
    if (!window.confirm('Die Buchungen dieses Laufs zurücknehmen? Bestände, Produktion und gekürzte Bestellungen stehen danach wieder wie vorher, der Lauf ist wieder offen.')) return
    void ausfuehren(async () => {
      const bericht = await api.zuruecknehmen(lauf.id)
      setErfolg(
        `Zurückgenommen: ${bericht.bewegungen} Bewegungen, ${bericht.produktion} Produktionen, ${bericht.plan} Planeinträge.` +
          (bericht.bestellungen.length > 0 ? ` Bestellungen wieder wie vorher: ${bericht.bestellungen.join('; ')}.` : '') +
          (bericht.offen_geblieben.length > 0 ? ` Von Hand prüfen: ${bericht.offen_geblieben.join('; ')}.` : '') +
          // Ein korrigierter Tagesbericht hatte ältere SelectLine-Läufe ersetzt - die sind jetzt wieder gebucht (02.11.2026).
          ((bericht.wieder_gebucht ?? []).length > 0
            ? ` Wieder gebucht: Lauf ${(bericht.wieder_gebucht ?? []).join(', ')} - die Läufe, die dieser ersetzt hatte.`
            : ''),
      )
      listeLaden()
      setLauf(await api.importLauf(lauf.id))
    })
  }

  function hochladen(datei: File) {
    void ausfuehren(async () => {
      const neu = await api.hochladen(datei)
      listeLaden()
      navigate(laufPfad(neu))
    })
  }

  function excelHochladen(datei: File) {
    void ausfuehren(async () => {
      const neu = await api.excelHochladen(datei)
      listeLaden()
      navigate(laufPfad({ id: neu.id, dokumenttyp: 'excel' }))
    })
  }

  function umschalten(positionId: number) {
    setAbgewaehlt((alt) => {
      const neu = new Set(alt)
      if (neu.has(positionId)) neu.delete(positionId)
      else neu.add(positionId)
      return neu
    })
  }

  return (
    <>
      <div className="leiste">
        <label className="hinweis">
          PDF hochladen{' '}
          <input
            type="file"
            accept="application/pdf"
            disabled={arbeitet}
            onChange={(e) => {
              const datei = e.target.files?.[0]
              if (datei) hochladen(datei)
              e.target.value = ''
            }}
          />
        </label>
        <label className="hinweis">
          Excel einlesen{' '}
          <input
            type="file"
            accept=".xlsx,.xlsm"
            disabled={arbeitet}
            title="Kladde, Kladde Material, Inventurlisten, Inventur-Auswertung, PLAN - IST: die Anwendung erkennt die Blätter, die KI prüft den Plan, geschrieben wird erst nach Ihrem Ja."
            onChange={(e) => {
              const datei = e.target.files?.[0]
              if (datei) excelHochladen(datei)
              e.target.value = ''
            }}
          />
        </label>
        <span className="hinweis">
          Reports an othuena@intsode.com werden automatisch abgeholt. Der PDF-Upload ist für Nachträge da; Excel wird
          als Plan gezeigt und erst nach Prüfung übernommen{arbeitet ? ' - die Analyse läuft, bei großen Dateien eine Minute…' : '.'}
        </span>
      </div>

      {!lauf && (
        <details className="karte" style={{ marginTop: 0 }}>
          <summary style={{ cursor: 'pointer' }}>
            <b>Was sich importieren lässt</b>
            <span className="hinweis"> - vier SelectLine-Reports als PDF, dazu Freitext und Zählzettel</span>
          </summary>
          <table style={{ marginTop: 8 }}>
            <tbody>
              <tr>
                <td>Ergebnismenge</td>
                <td className="hinweis">Monatsreport „Produktion von … bis": Produktion je Fertigartikel und Materialverbrauch. Wird gebucht.</td>
              </tr>
              <tr>
                <td>Artikelbestandsliste</td>
                <td className="hinweis">„Bestand zum …" je Lager: das SelectLine-Soll zum Stichtag. Reiner Vergleichswert für die Inventur.</td>
              </tr>
              <tr>
                <td>Inventur-Historie</td>
                <td className="hinweis">„Beleg INV…": gezählte Bestände je Charge mit Ist und Soll und Bewertung. Die Ist-Mengen werden gebucht.</td>
              </tr>
              <tr>
                <td>Wareneingang</td>
                <td className="hinweis">„Wareneingang WE…": Zugänge je Artikel zum Liefertermin. Wird gebucht.</td>
              </tr>
              <tr>
                <td>Freitext</td>
                <td className="hinweis">
                  Zählzettel, Wochenplan, Wareneingang mit Charge, Produktion nachtragen, Korrektur - über <Link to="/erfassen">Erfassen</Link> oder den{' '}
                  <Link to="/assistent">Assistenten</Link>. Landet hier als Import.
                </td>
              </tr>
              <tr>
                <td>Inventur</td>
                <td className="hinweis">Der Abschluss einer <Link to="/inventur">Inventur</Link> erzeugt einen Import mit den Zählbeständen.</td>
              </tr>
            </tbody>
          </table>
          <p className="hinweis" style={{ marginBottom: 0 }}>
            Ein unbekanntes Dokument wird nicht stumm abgelehnt: das System beschreibt, was es sieht, und stellt eine Rückfrage.
            Gebucht wird nichts vor der Freigabe.
          </p>
        </details>
      )}

      <Meldungen fehler={listeFehler ?? fehler} erfolg={erfolg} />

      {!lauf && (
        <>
          <h2>Importe</h2>
          <div className="tabellenrahmen">
            <table>
              <thead>
                <tr>
                  <th>Angelegt</th>
                  <th>Quelle</th>
                  <th>Dokument</th>
                  <th>Typ</th>
                  <th className="zahl">Positionen</th>
                  <th className="zahl">Artikel fehlen</th>
                  <th className="zahl">Rückfragen</th>
                  <th>Status</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {liste.map((eintrag) => (
                  <tr key={eintrag.id}>
                    <td>{zeitpunkt(eintrag.angelegt_am)}</td>
                    <td>{eintrag.quelle}</td>
                    <td>
                      {istKorrigiert(eintrag) ? (
                        <span title={eintrag.dateiname ?? undefined}>korrigierter Tagesbericht</span>
                      ) : (
                        (eintrag.dateiname ?? '-')
                      )}
                    </td>
                    <td>{eintrag.dokumenttyp}</td>
                    <td className="zahl">{eintrag.anzahl_positionen}</td>
                    <td className="zahl">
                      {eintrag.anzahl_fehlende_artikel > 0 ? eintrag.anzahl_fehlende_artikel : ''}
                    </td>
                    <td className="zahl">
                      {eintrag.anzahl_rueckfragen > 0 ? eintrag.anzahl_rueckfragen : ''}
                    </td>
                    <td>
                      <span className={`status ${eintrag.status}`}>
                        {IMPORTSTATUS[eintrag.status].text}
                      </span>
                      {eintrag.entschieden_von === SELBST_GEBUCHT && <span className="hinweis"> automatisch</span>}
                    </td>
                    <td>
                      <button onClick={() => navigate(`/importe/${eintrag.id}`)}>Ansehen</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {liste.length === 0 && !listeFehler && <p className="leer">Noch keine Importe.</p>}
          </div>
        </>
      )}

      {lauf && (
        <>
          <div className="leiste">
            <button onClick={() => navigate('/importe')}>Zurück zur Liste</button>
            <h2>
              Import {lauf.id} · {istKorrigiert(lauf) ? 'Korrigierter Tagesbericht' : (lauf.dateiname ?? lauf.dokumenttyp)}
            </h2>
            <span className={`status ${lauf.status}`}>{IMPORTSTATUS[lauf.status].text}</span>
            {lauf.entschieden_von === SELBST_GEBUCHT && (
              <span className="hinweis">ohne Klick gebucht - unauffällig aus SelectLine</span>
            )}
            <span className="hinweis" title={`ausgewertet mit ${lauf.modell ?? 'unbekanntem Modell'}`}>
              {zeitpunkt(lauf.angelegt_am)}
            </span>
          </div>

          {/* Nur Fehler und Warnungen sind orange; ein Hinweis bleibt grau, damit der Fehler auffällt. */}
          {lauf.befunde.map((befund, index) => (
            <Meldung key={index} art={befund.schwere} titel={BEFUNDTITEL[befund.schwere]}>
              {befund.text}
            </Meldung>
          ))}

          {istKorrigiert(lauf) && (
            <Meldung art="hinweis" titel="Korrigierter Tagesbericht">
              Die nächtliche Nachprüfung hat für schon gebuchte Tage andere Zahlen aus SelectLine bekommen - etwa eine
              Produktion, die erst nach dem Abruf gebucht wurde. Freigeben nimmt die älteren SelectLine-Läufe dieser Tage
              zurück und verwirft sie; Zurücknehmen dieses Laufs bucht sie wieder. Verwerfen heißt: so lassen, die
              Plattform meldet dieselbe Abweichung nicht noch einmal.
            </Meldung>
          )}

          {lauf.struktur && (
            <Meldung art="hinweis" titel="So beschreibt das Modell dieses Dokument">
              {lauf.struktur}
            </Meldung>
          )}

          {(lauf.doppelt?.length ?? 0) > 0 && (
            <Meldung art="warnung" titel="Diese Datei wurde schon einmal geladen">
              {doppeltText(lauf.doppelt ?? [])}{' '}
              {(lauf.doppelt ?? []).map((d) => (
                <Link key={d.lauf_id} to={`/importe/${d.lauf_id}`} style={{ marginRight: 8 }}>
                  Import {d.lauf_id} ansehen
                </Link>
              ))}
            </Meldung>
          )}

          {freigabeHinweise.length > 0 && (
            <Meldung art="hinweis" titel="Hinweise zur Freigabe">
              <ul style={{ margin: '4px 0 0', paddingLeft: 18 }}>
                {freigabeHinweise.map((h, i) => (
                  <li key={i}>{h}</li>
                ))}
              </ul>
            </Meldung>
          )}

          {uebergangen.length > 0 && (
            <Meldung art="warnung" titel="Beim Buchen übergangen">
              {uebergangen.length === 1 ? 'Diese Rückfrage war' : `Diese ${uebergangen.length} Rückfragen waren`} noch offen und
              steht jetzt auf „übergangen“ - eine Antwort lässt sich nachtragen:
              <ul>
                {uebergangen.map((r) => (
                  <li key={r.id}>{r.frage}</li>
                ))}
              </ul>
              <Link to="/rueckfragen#uebergangen">Antwort nachtragen</Link>
            </Meldung>
          )}

          {lauf.status === 'gesperrt' && (
            <Meldung art="fehler" titel="Dieser Import lässt sich nicht freigeben">
              Eine Kontrollrechnung ist fehlgeschlagen. Die Ursache steht oben. Zu jedem Punkt, den
              Sie beantworten können, steht unten eine Rückfrage - antworten Sie in eigenen Worten.
              Ist die Ursache behoben, genügt Erneut prüfen; das Dokument muss nicht noch einmal
              eingelesen werden.
            </Meldung>
          )}

          {lauf.status !== 'gebucht' && lauf.status !== 'verworfen' && (
            <Rueckfragen
              key={lauf.id}
              importLaufId={lauf.id}
              onGeaendert={() => {
                laufLaden()
                listeLaden()
              }}
            />
          )}

          {lauf.status !== 'gebucht' && lauf.status !== 'verworfen' && (
            <div className="leiste">
              <button disabled={arbeitet} onClick={neuPruefen}>
                Erneut prüfen
              </button>
              <span className="hinweis">
                Rechnet denselben Lauf gegen die aktuellen Stammdaten nach. Die KI wird dabei nicht
                erneut gefragt.
                {lauf.geprueft_am ? ` Zuletzt geprüft: ${zeitpunkt(lauf.geprueft_am)}.` : ''}
              </span>
            </div>
          )}

          {lauf.anzahl_fehlende_artikel > 0 &&
            lauf.status !== 'gebucht' &&
            lauf.status !== 'verworfen' && (
              <FehlendeArtikel
                laufId={lauf.id}
                onFertig={() => {
                  laufLaden()
                  listeLaden()
                  setErfolg('Stammdaten ergänzt und erneut geprüft.')
                }}
              />
            )}

          <div className="tabellenrahmen">
            <table>
              <thead>
                <tr>
                  <th>Übernehmen</th>
                  <th>Artikel</th>
                  <th>Bezeichnung</th>
                  <th>Tag</th>
                  <th>Charge</th>
                  <th>Rolle</th>
                  <th className="zahl">Beleg</th>
                  <th className="zahl">in t</th>
                  <th className="zahl">alt</th>
                  <th className="zahl">neu</th>
                  <th className="zahl">Differenz</th>
                  <th>Hinweis</th>
                </tr>
              </thead>
              <tbody>
                {lauf.positionen.map((position) => (
                  <tr key={position.id}>
                    <td>
                      <input
                        type="checkbox"
                        checked={!abgewaehlt.has(position.id)}
                        disabled={lauf.status !== 'offen'}
                        onChange={() => umschalten(position.id)}
                      />
                    </td>
                    <td>{position.artikelnummer}</td>
                    <td>{position.bezeichnung ?? ''}</td>
                    <td title={position.tag_quelle ?? ''}>
                      {datum(position.tag)}
                    </td>
                    <td>{position.charge ?? ''}</td>
                    <td>{position.rolle ? (ROLLENTEXT[position.rolle] ?? position.rolle) : ''}</td>
                    <td className="zahl">
                      {menge(position.menge_original)} {position.einheit_original ?? ''}
                    </td>
                    <td className="zahl">{tonnen(position.menge_t)}</td>
                    <td className="zahl">{tonnen(position.alt_wert_t)}</td>
                    <td className="zahl">{tonnen(position.neu_wert_t)}</td>
                    <td className="zahl">{differenz(position.differenz_t)}</td>
                    <td>
                      {[position.hinweis, position.wert_hinweis].filter(Boolean).join(' ')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div style={{ marginTop: 12 }}>
            <Zweitblick laufId={lauf.id} aktiv={lauf.status === 'offen' || lauf.status === 'gesperrt'} />
          </div>
          {lauf.status === 'gebucht' && (
            <div className="leiste" style={{ marginTop: 14 }}>
              <button disabled={arbeitet} onClick={zuruecknehmen}>
                Buchung zurücknehmen
              </button>
              <span className="hinweis">
                Dreht Bestände, Produktion und gekürzte Bestellungen zurück; der Lauf ist danach wieder offen.
              </span>
            </div>
          )}
          {lauf.status === 'offen' && (
            <div className="leiste" style={{ marginTop: 14 }}>
              <button className="haupt" disabled={arbeitet} onClick={freigeben}>
                Freigeben und buchen
              </button>
              <button disabled={arbeitet} onClick={verwerfen}>
                Verwerfen
              </button>
              <span className="hinweis">
                {lauf.positionen.filter((p) => wirdGebucht(p) && !abgewaehlt.has(p.id)).length} von{' '}
                {lauf.positionen.length} Positionen werden übernommen
                {lauf.positionen.some((p) => !wirdGebucht(p))
                  ? ` (${lauf.positionen.filter((p) => !wirdGebucht(p)).length} nicht buchbar, siehe Hinweise in der Tabelle).`
                  : '.'}
              </span>
            </div>
          )}
        </>
      )}
    </>
  )
}
